import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { ALLERGENS } from "../lib/menuConstants";
import { tContent } from "../lib/i18n";
import { LANG_OPTIONS } from "../lib/i18n";
import { useLang } from "../context/LanguageContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Globe, ArrowLeft } from "@phosphor-icons/react";

const HEADING = {
  va: { title: "Al·lèrgens", subtitle: "Informació d'al·lèrgens per plat", filter: "Filtra per al·lergen", all: "Tots", none: "Cap al·lergen declarat", empty: "No hi ha plats amb aquest filtre.", back: "Tornar a la carta", legal: "Informació sobre al·lèrgens segons el Reglament UE 1169/2011.", notice: "IMPORTANT: Com que tots els nostres productes són elaborats de forma casolana i artesanal, poden contindre traces de qualsevol al·lergen.", photos: "AVÍS: Les fotografies són merament orientatives i algunes han sigut generades mitjançant IA, per la qual cosa poden no coincidir exactament amb el producte final." },
  es: { title: "Alérgenos", subtitle: "Información de alérgenos por plato", filter: "Filtrar por alérgeno", all: "Todos", none: "Sin alérgenos declarados", empty: "No hay platos con este filtro.", back: "Volver a la carta", legal: "Información sobre alérgenos según el Reglamento UE 1169/2011.", notice: "IMPORTANTE: Como todos nuestros productos son elaborados de forma casera y artesanal, pueden contener trazas de cualquier alérgeno.", photos: "AVISO: Las fotografías son meramente orientativas y algunas han sido generadas mediante IA, por lo que pueden no coincidir exactamente con el producto final." },
  en: { title: "Allergens", subtitle: "Allergen information by dish", filter: "Filter by allergen", all: "All", none: "No allergens declared", empty: "No dishes match this filter.", back: "Back to menu", legal: "Allergen information under EU Regulation 1169/2011.", notice: "IMPORTANT: All our products are homemade and artisanal, so they may contain traces of any allergen.", photos: "NOTICE: Photographs are for illustrative purposes only and some have been generated using AI, so they may not exactly match the final product." },
  fr: { title: "Allergènes", subtitle: "Informations d'allergènes par plat", filter: "Filtrer par allergène", all: "Tous", none: "Aucun allergène déclaré", empty: "Aucun plat ne correspond à ce filtre.", back: "Retour au menu", legal: "Informations sur les allergènes selon le Règlement UE 1169/2011.", notice: "IMPORTANT : Tous nos produits sont faits maison et artisanaux, ils peuvent donc contenir des traces de n'importe quel allergène.", photos: "AVIS : Les photographies sont purement illustratives et certaines ont été générées par IA, elles peuvent donc ne pas correspondre exactement au produit final." },
};

export default function AllergensPage() {
  const { lang, setLang } = useLang();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState("all"); // 'all' | 'none' | allergen id
  const [barName, setBarName] = useState("");

  const L = HEADING[lang] || HEADING.va;

  useEffect(() => {
    Promise.all([api.get("/menu-items"), api.get("/categories"), api.get("/settings/bar")])
      .then(([m, c, s]) => {
        setItems(m.data);
        setCategories(c.data);
        setBarName(s.data.bar_name || "");
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const topLevels = useMemo(
    () => categories.filter((c) => !c.parent_id).sort((a, b) => a.order - b.order),
    [categories]
  );

  const filtered = useMemo(() => {
    const avail = items.filter((i) => i.available);
    if (filter === "all") return avail;
    if (filter === "none") return avail.filter((i) => (i.allergens || []).length === 0);
    return avail.filter((i) => (i.allergens || []).includes(filter));
  }, [items, filter]);

  const grouped = useMemo(() => {
    const byCat = {};
    for (const it of filtered) byCat[it.category_id] = (byCat[it.category_id] || []).concat(it);
    return topLevels
      .map((cat) => {
        const kidIds = categories.filter((c) => c.parent_id === cat.id).map((k) => k.id);
        const catItems = [...(byCat[cat.id] || []), ...kidIds.flatMap((k) => byCat[k] || [])];
        return { cat, items: catItems.sort((a, b) => a.name.localeCompare(b.name)) };
      })
      .filter((g) => g.items.length > 0);
  }, [filtered, topLevels, categories]);

  return (
    <div className="carta-scope min-h-screen bg-background text-foreground pb-20">
      {/* Top bar */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-3 px-4 py-3">
          <Link to="/" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition" data-testid="back-to-menu">
            <ArrowLeft size={18} weight="bold" /> {L.back}
          </Link>
          <div className="flex items-center gap-3">
            <Globe size={18} className="text-muted-foreground" />
            <Select value={lang} onValueChange={setLang}>
              <SelectTrigger className="h-9 w-32 bg-transparent border-white/20 text-xs uppercase tracking-widest" data-testid="lang-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANG_OPTIONS.map((l) => (
                  <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="max-w-5xl mx-auto px-4 pt-10 pb-6">
        <div className="text-xs uppercase tracking-widest text-primary mb-2">{barName}</div>
        <h1 className="font-serif text-5xl md:text-6xl font-light" data-testid="allergens-title">{L.title}</h1>
        <p className="text-muted-foreground mt-2">{L.subtitle}</p>
      </header>

      {/* Filter chips */}
      <div className="max-w-5xl mx-auto px-4 mb-8">
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{L.filter}</div>
        <div className="flex flex-wrap gap-2">
          <FilterChip active={filter === "all"} onClick={() => setFilter("all")} testid="allergen-filter-all">
            {L.all}
          </FilterChip>
          <FilterChip active={filter === "none"} onClick={() => setFilter("none")} testid="allergen-filter-none">
            ✓ {L.none}
          </FilterChip>
          {ALLERGENS.map((a) => (
            <FilterChip key={a.id} active={filter === a.id} onClick={() => setFilter(a.id)} testid={`allergen-filter-${a.id}`}>
              {a.icon} {a.label}
            </FilterChip>
          ))}
        </div>
      </div>

      {/* Grouped items */}
      <main className="max-w-5xl mx-auto px-4">
        {grouped.length === 0 && (
          <p className="text-muted-foreground text-center py-16" data-testid="allergens-empty">{L.empty}</p>
        )}
        {grouped.map(({ cat, items: catItems }) => (
          <section key={cat.id} className="mb-12" data-testid={`allergens-section-${cat.id}`}>
            <div className="flex items-baseline gap-4 mb-4">
              <h2 className="font-serif text-3xl font-light">{tContent(cat, lang).name}</h2>
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs uppercase tracking-widest text-muted-foreground">{catItems.length}</span>
            </div>
            <div className="space-y-2">
              {catItems.map((it) => {
                const list = (it.allergens || []).map((id) => ALLERGENS.find((a) => a.id === id)).filter(Boolean);
                const c = tContent(it, lang);
                return (
                  <div key={it.id} className="flex items-start justify-between gap-4 py-3 border-b border-border/50" data-testid={`allergen-row-${it.id}`}>
                    <div className="min-w-0">
                      <p className="font-serif text-lg leading-snug">{c.name}</p>
                      {c.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{c.description}</p>}
                    </div>
                    <div className="flex flex-wrap gap-1 shrink-0 justify-end max-w-[55%]">
                      {list.length === 0 ? (
                        <span className="text-xs px-2 py-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-500">
                          {L.none}
                        </span>
                      ) : (
                        list.map((a) => (
                          <span key={a.id} title={a.label} className="text-xs px-2 py-1 rounded-full border border-border bg-secondary/60 flex items-center gap-1">
                            <span>{a.icon}</span>
                            <span className="hidden sm:inline">{a.label}</span>
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}

        <p className="text-xs italic text-muted-foreground text-center mt-16 border-t border-border pt-6" data-testid="allergen-legal">
          {L.notice}
        </p>
        <p className="text-xs italic text-muted-foreground text-center mt-2" data-testid="photos-legal">
          {L.photos}
        </p>
        <p className="text-xs text-muted-foreground text-center mt-2">{L.legal}</p>
      </main>
    </div>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs uppercase tracking-wider border transition-colors ${
        active
          ? "bg-primary text-primary-foreground border-primary"
          : "border-border text-muted-foreground hover:text-foreground hover:border-white/30"
      }`}
    >
      {children}
    </button>
  );
}
